import { Box, Button, Flex, HStack, Link, VStack, useToast } from "@chakra-ui/react";
import { dayjs } from "@equidefi/i18n";
import { useDocumentTitle } from "@equidefi/portals/hooks/useDocumentTitle";
import { PAYMENT_FAILED_STATUSES } from "@equidefi/shared/constants/payments";
import { isPaymentAdjustment } from "@equidefi/shared/helpers/payments";
import type { DTO } from "@equidefi/shared/types/dto";
import { BannerAlert, Heading, Paper, Text } from "@equidefi/ui";
import { Icon } from "@equidefi/ui/icon";
import type React from "react";
import { useMemo } from "react";
import { useNavigate, useParams } from "react-router";

import { PAYMENT_TYPE_CONFIG, PaymentStatusBadge } from "@/components/payment/PaymentInfoCard";
import { CurrencyFormatter } from "@/constants/format";
import { PAYMENT_STATUS_DISPLAY } from "@/constants/payments";
import { VAULT_ROUTES } from "@/constants/routes";
import { useInitiatePaymentMutation, useInvestment } from "@/hooks/useInvestments";
import { useOfferingV2 } from "@/hooks/useOfferings";
import { EquidefiMarkdown } from "@equidefi/portals/components/Markdown";
import { StripeWireInstructionsCard } from "@equidefi/portals/components/payment/StripeWireInstructionsCard";
import { VaultContainer } from "../../components";
import { InvestmentTitleCard, SectionButton, VaultPageHeader } from "../components";

/**
 * ManualPaymentInstructions - Displays wire/check instructions
 */
interface ManualPaymentInstructionsProps {
  offering: DTO.Offering | null | undefined;
  payment: DTO.Payment;
}

const ManualPaymentInstructions: React.FC<ManualPaymentInstructionsProps> = ({ offering, payment }) => {
  if (!offering) return null;

  const paymentType = payment.payment_type?.toLowerCase();
  const instructions =
    paymentType === "wire" ? offering?.manual_payment_wire_instructions : offering?.manual_payment_check_instructions;

  if (!instructions || instructions.length === 0) return null;

  return (
    <Paper bg="white" borderWidth="1px" borderColor="gray.200" p="6" boxShadow="sm">
      <Heading fontSize="xl" fontWeight="bold" mb="4">
        Payment Instructions
      </Heading>

      <Text fontSize="md" mb="4" color="gray.700">
        {paymentType === "wire"
          ? "Please initiate your wire to the account listed below:"
          : "Include the investment amount and send your check to:"}
      </Text>

      <Box borderWidth="1px" borderRadius="md" p="4">
        <EquidefiMarkdown fontSize="sm">{instructions}</EquidefiMarkdown>
        <HStack spacing={4} mt={4}>
          <Link
            href={VAULT_ROUTES.printPayments(payment.investment_id, paymentType || "")}
            target="_blank"
            rel="noreferrer"
            color="blue.600"
            display="inline-flex"
            alignItems="center"
            gap={2}
            fontWeight="medium">
            <Icon.Printer size="1em" />
            Print Payment Instructions
          </Link>
        </HStack>
      </Box>
    </Paper>
  );
};

/**
 * SinglePaymentCard - Displays single payment in card format (mobile-friendly)
 */
interface SinglePaymentCardProps {
  payment: DTO.Payment;
  showActions?: boolean;
  showDeclineReason?: boolean;
  onRetry?: () => void;
  onChangeMethod?: () => void;
  isRetrying?: boolean;
}

const SinglePaymentCard: React.FC<SinglePaymentCardProps> = ({
  payment,
  showActions = true,
  showDeclineReason = false,
  onRetry,
  onChangeMethod,
  isRetrying,
}) => {
  const paymentConfig = PAYMENT_TYPE_CONFIG[payment.payment_type];

  const PaymentIcon = paymentConfig?.icon || Icon.DollarSign;
  const isExpired = payment.is_expired || PAYMENT_FAILED_STATUSES.includes(payment.status);
  const canRetry = isExpired && payment.category === "STRIPE";
  const canChangeMethod = (payment.status === "created" || isExpired) && !canRetry;

  const isSucceeded = payment.status === "succeeded";
  const showDate = !isSucceeded;
  const dateLabel = isExpired ? "Expired on" : "Indicated on";

  return (
    <VStack align="stretch" spacing="3" mt="4">
      {/* Amount */}
      <Text fontSize="3xl" fontWeight="bold" m="0">
        {CurrencyFormatter.format(Number(payment.amount))}
      </Text>

      {/* Payment Method and Status */}
      <Flex justify="space-between" align="center" flexWrap="wrap" gap="2">
        <Text fontSize="md" m="0">
          {payment.payment_method_display || paymentConfig?.label || payment.payment_type}
        </Text>
        <PaymentStatusBadge payment={payment} />
      </Flex>

      {/* Date - only show for non-succeeded payments */}
      {showDate && (
        <Text fontSize="sm" color="gray.600" m="0">
          {dateLabel} {dayjs(payment.expiration_date || payment.create_date).format("MMM Do, YYYY")}
        </Text>
      )}

      {/* Decline reason */}
      {showDeclineReason && payment.error_msg && (
        <Text fontSize="sm" color="red.500" m="0" whiteSpace="normal">
          {payment.error_msg}
        </Text>
      )}

      {/* Actions */}
      {showActions && (canRetry || canChangeMethod) && (
        <VStack spacing="2" mt="2">
          {canRetry && (
            <Button
              colorScheme="purple"
              onClick={onRetry}
              isLoading={isRetrying}
              loadingText="Creating Payment..."
              w="full"
              size="lg">
              Retry Payment
            </Button>
          )}
          {canChangeMethod && (
            <Button colorScheme="purple" onClick={onChangeMethod} w="full" size="lg">
              Change Payment Method
            </Button>
          )}
        </VStack>
      )}
    </VStack>
  );
};

/**
 * EmptyState - No payments created
 */
const EmptyState: React.FC = () => {
  return (
    <Box mt="4" pt="4" borderTop="1px" borderColor="gray.200">
      <Text fontSize="md" color="gray.400" m="0">
        No payment has been created yet.
      </Text>
    </Box>
  );
};

/**
 * PaymentsPage - Main Payments subpage component
 *
 * Features:
 * - Single payment: Card layout
 * - Multiple payments: Stacked cards
 * - Latest payment determines banner and actions
 * - Manual payment instructions shown when applicable
 */
const PaymentsPage: React.FC = () => {
  useDocumentTitle(["Payments"]);

  const navigate = useNavigate();
  const toast = useToast();
  const { id } = useParams<{ id: string }>();

  const { data: investment, isLoading } = useInvestment(id, {
    refetchOnMount: true,
  });

  // Fetch full offering data for payment instructions
  const { data: fullOffering } = useOfferingV2(investment?.offering?.id);

  const createPayment = useInitiatePaymentMutation(id || "", {
    onSuccess: () => {
      toast({
        status: "success",
        description: "Payment retry initiated. Redirecting to payment page...",
      });
      navigate(VAULT_ROUTES.payment(id || ""));
    },
    onError: (error: Error) => {
      toast({
        status: "error",
        title: "Payment Retry Failed",
        description: error?.message || "Failed to initiate payment retry. Please try again.",
      });
    },
  });

  const offering = investment?.offering;
  const issuer = investment?.issuer;
  // Adjustments are issuer records, not money the investor sent.
  const payments = (investment?.payments || []).filter((payment) => !isPaymentAdjustment(payment));
  // Payments are sorted newest-first, so [0] is the latest payment
  // Returns undefined if array is empty, which is handled by checks below
  const latestPayment = payments.length > 0 ? payments[0] : undefined;
  const hasMultiplePayments = payments.length > 1;

  // Status banner based on latest payment
  const paymentStatusConfig = useMemo(() => {
    if (!latestPayment) return null;
    const config = PAYMENT_STATUS_DISPLAY[latestPayment.status] || PAYMENT_STATUS_DISPLAY.created;
    return {
      status: config.status,
      title: typeof config.title === "function" ? config.title(latestPayment) : config.title,
      description: typeof config.description === "function" ? config.description(latestPayment) : config.description,
    };
  }, [latestPayment]);

  const handleRetryPayment = () => {
    createPayment.mutate();
  };

  const handleChangeMethod = () => {
    navigate(VAULT_ROUTES.payment(id || ""));
  };

  // Loading state
  if (isLoading || !investment) {
    return (
      <VaultContainer>
        <SectionButton to={VAULT_ROUTES.investmentDetails(id || "")} iconDirection="left">
          Back To Investment
        </SectionButton>
        <Text mt="4">Loading payment details...</Text>
      </VaultContainer>
    );
  }

  // Error state
  if (!id) {
    return (
      <VaultContainer>
        <BannerAlert status="error" title="Invalid Investment" description="No investment ID provided." />
      </VaultContainer>
    );
  }

  return (
    <VaultContainer pb="10" maxW={{ base: "560px", lg: "1040px" }}>
      <VaultPageHeader
        crumbs={[
          { label: "Portfolio", to: "/vault/dashboard" },
          { label: issuer?.name ?? "Issuer", to: VAULT_ROUTES.offering(offering?.id ?? "") },
          { label: offering?.name ?? "Offering", to: VAULT_ROUTES.offering(offering?.id ?? "") },
          { label: "Investment", to: VAULT_ROUTES.investmentDetails(id) },
          { label: "Payment" },
        ]}
        backTo={VAULT_ROUTES.investmentDetails(id)}
        backLabel="Back To Investment"
      />

      <VStack spacing="4" align="stretch">
        <InvestmentTitleCard investment={investment} issuer={issuer} offering={offering} />

        {/* Main Payment Card */}
        <Paper bg="white" p={{ base: "4", md: "6" }} boxShadow="md">
          <Heading fontWeight="bold" mb="2" fontSize={{ base: "xl", md: "2xl" }}>
            Payments
          </Heading>

          <Text mb="4" color="gray.600" fontSize={{ base: "sm", md: "md" }}>
            Your payment details are shown below.
          </Text>

          {/* Status Banner */}
          {paymentStatusConfig && (
            <BannerAlert
              status={paymentStatusConfig.status as "success" | "warning" | "error" | "info"}
              title={paymentStatusConfig.title}
              description={paymentStatusConfig.description}
            />
          )}

          {/* Payment Display */}
          {!latestPayment && <EmptyState />}

          {latestPayment && !hasMultiplePayments && (
            <SinglePaymentCard
              payment={latestPayment}
              onRetry={handleRetryPayment}
              onChangeMethod={handleChangeMethod}
              isRetrying={createPayment.isLoading}
            />
          )}

          {hasMultiplePayments && (
            <VStack spacing="3" mt="4" align="stretch">
              {payments.map((payment, index) => (
                <Paper key={payment.id} p="3" borderWidth="1px" borderColor="gray.200" bg="gray.50">
                  <SinglePaymentCard
                    payment={payment}
                    showActions={index === 0}
                    showDeclineReason
                    onRetry={handleRetryPayment}
                    onChangeMethod={handleChangeMethod}
                    isRetrying={createPayment.isLoading}
                  />
                </Paper>
              ))}
            </VStack>
          )}
        </Paper>

        {/* Manual Payment Instructions */}
        {latestPayment?.category === "MANUAL" && latestPayment?.status === "created" && (
          <ManualPaymentInstructions offering={fullOffering} payment={latestPayment} />
        )}

        {/* Stripe Wire Instructions */}
        {latestPayment?.stripe_wire_instructions && (
          <Paper bg="white" borderWidth="1px" borderColor="gray.200" p="6" boxShadow="sm">
            <Heading fontSize="xl" fontWeight="bold" mb="4">
              Wire Instructions
            </Heading>
            <Text fontSize="md" mb="4" color="gray.700">
              Send your wire to the account below. Include the reference in your wire memo so we can match the payment
              to your investment.
            </Text>
            <StripeWireInstructionsCard
              instructions={latestPayment.stripe_wire_instructions}
              investmentId={latestPayment.investment_id}
              enablePrint
            />
          </Paper>
        )}
      </VStack>
    </VaultContainer>
  );
};

export default PaymentsPage;
